import { useMemo, useState } from 'react';
import type { Category } from '@inference.engine/guidelines';
import { useDeleteGuideline } from '@/features/guidelines/hooks';
import type { GuidelineResponseDto } from '@/lib/api/guidelines-client';
import type {
  SortableGuidelineKeys,
  SortDirection,
} from '@/features/guidelines/ui/GuidelinesDatatable/GuidelinesDatatable.types';
import type {
  UseFolderGuidelinesPaneParams,
  UseFolderGuidelinesPaneReturn,
} from './useFolderGuidelinesPane.types';

/**
 * Client-side filter, sort, and dialog state for the active folder's
 * guidelines. The whole folder arrives in a single `useGuidelinesByFolder`
 * response, so filtering and sorting run over data already in the client —
 * a paginator would page over bytes already downloaded, and a debounce
 * would only add latency to a synchronous `useMemo`. No `page`, `pageSize`,
 * or `totalCount` exists on the return type: there is nothing to wire a
 * paginator to.
 */
export function useFolderGuidelinesPane({
  guidelines,
  folderId,
}: UseFolderGuidelinesPaneParams): UseFolderGuidelinesPaneReturn {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortableGuidelineKeys>('created');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [guidelineToEdit, setGuidelineToEdit] = useState<GuidelineResponseDto | null>(null);
  const [guidelineToDelete, setGuidelineToDelete] = useState<GuidelineResponseDto | null>(null);

  const { mutate: deleteGuideline, isPending: isDeleting } = useDeleteGuideline();

  const filteredGuidelines = useMemo(() => {
    let result = guidelines;

    if (selectedCategories.length > 0) {
      result = result.filter((g) => selectedCategories.includes(g.category));
    }

    const query = searchQuery.trim().toLowerCase();
    if (query) {
      result = result.filter(
        (g) => g.name.toLowerCase().includes(query) || g.description.toLowerCase().includes(query),
      );
    }

    return result;
  }, [guidelines, searchQuery, selectedCategories]);

  const visibleGuidelines = useMemo(() => {
    return [...filteredGuidelines].sort((a, b) => {
      if (sortBy === 'created') {
        const comparison = new Date(a.created).getTime() - new Date(b.created).getTime();
        return sortDirection === 'asc' ? comparison : -comparison;
      }

      const aValue = a[sortBy];
      const bValue = b[sortBy];
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortDirection === 'asc' ? comparison : -comparison;
      }

      return 0;
    });
  }, [filteredGuidelines, sortBy, sortDirection]);

  return {
    visibleGuidelines,
    searchQuery,
    sortBy,
    sortDirection,
    selectedCategories,
    dialogs: {
      createOpen,
      guidelineToEdit,
      guidelineToDelete,
      isDeleting,
    },
    handlers: {
      onSearchChange: setSearchQuery,
      onSort: (key, direction) => {
        setSortBy(key);
        setSortDirection(direction);
      },
      onCategoryFilterChange: setSelectedCategories,
      onOpenCreate: () => setCreateOpen(true),
      onCloseCreate: () => setCreateOpen(false),
      onEditRow: setGuidelineToEdit,
      onCloseEdit: () => setGuidelineToEdit(null),
      onDeleteRow: setGuidelineToDelete,
      onCancelDelete: () => setGuidelineToDelete(null),
      onConfirmDelete: () => {
        if (!guidelineToDelete || isDeleting) return;
        deleteGuideline(
          {
            guidelineId: guidelineToDelete.id,
            guidelineName: guidelineToDelete.name,
            folderId: folderId ?? undefined,
          },
          { onSuccess: () => setGuidelineToDelete(null) },
        );
      },
    },
  };
}
